import { useMemo, useState } from 'react'
import type { Proyecto, Tarea } from '@/lib/types'
import { r2 } from '@/lib/utils'
import { IconTimer, IconAlertTriangle, IconFileText } from '@/components/icons/Icons'

/*
  Consumo de las bolsas de horas de un contrato de soporte.

  Un contrato de mantenimiento no vende "10 horas": vende horas con destino.
  En FUTURA son 4 h de preventivo, 4 de reactivo y 2 de evolutivas, y pasarse
  en una NO se compensa con que sobre en otra — cada una tiene su tope. Este
  bloque es lo que permite decir "esto entra en el mes, esto se cotiza aparte"
  antes de haberlo hecho gratis.

  De donde sale cada numero:
  - la bolsa y su tope: `proyectos.bolsas`
  - a que bolsa pertenece una tarea: etiqueta `bolsa:<clave>`
  - a que mes pertenece: etiqueta `ciclo:AAAA-MM`
  - las horas: SOLO de las hojas. Un bloque padre va en cero a proposito, para
    que agrupar tareas no duplique el consumo.
*/

const PREFIJO_BOLSA = 'bolsa:'
const PREFIJO_CICLO = 'ciclo:'

function etiquetasDe(t: Tarea): string[] {
  return Array.isArray(t.etiquetas) ? (t.etiquetas as unknown as string[]) : []
}

function valorDe(t: Tarea, prefijo: string): string | null {
  const e = etiquetasDe(t).find(x => typeof x === 'string' && x.startsWith(prefijo))
  return e ? e.slice(prefijo.length) : null
}

function cicloActual(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function nombreDeCiclo(ciclo: string): string {
  const [a, m] = ciclo.split('-')
  const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
  const nombre = meses[Number(m) - 1]
  return nombre ? `${nombre} ${a}` : ciclo
}

interface Props {
  proyecto: Proyecto
  tareas: Tarea[]
}

export default function ConsumoBolsas({ proyecto, tareas }: Props) {
  const bolsas = Array.isArray(proyecto.bolsas) ? proyecto.bolsas : []

  // Los ciclos que existen en las tareas, del mas nuevo al mas viejo. El mes
  // en curso va primero aunque todavia no tenga tareas: es el que se mira.
  const ciclos = useMemo(() => {
    const set = new Set<string>([cicloActual()])
    tareas.forEach(t => { const c = valorDe(t, PREFIJO_CICLO); if (c) set.add(c) })
    return [...set].sort().reverse()
  }, [tareas])

  const [ciclo, setCiclo] = useState<string>(ciclos[0] ?? cicloActual())

  const consumo = useMemo(() => {
    // Solo hojas: un bloque padre no suma, sus hijas si.
    const idsConHijas = new Set(tareas.map(t => t.parent_id).filter(Boolean) as number[])
    const hojas = tareas.filter(t => !idsConHijas.has(t.id))
    const delCiclo = hojas.filter(t => valorDe(t, PREFIJO_CICLO) === ciclo)

    const porBolsa = bolsas.map(b => {
      const suyas = delCiclo.filter(t => valorDe(t, PREFIJO_BOLSA) === b.clave)
      const comprometidas = r2(suyas.reduce((s, t) => s + (t.horas_estimadas || 0), 0))
      const consumidas = r2(suyas.reduce((s, t) => s + (t.horas_reales || 0), 0))
      const hechas = suyas.filter(t => t.estado === 'completada').length
      return {
        ...b,
        comprometidas,
        consumidas,
        tareas: suyas.length,
        hechas,
        excedente: r2(Math.max(0, comprometidas - b.horas_mes)),
        libres: r2(Math.max(0, b.horas_mes - comprometidas)),
        pct: b.horas_mes > 0 ? Math.round((comprometidas / b.horas_mes) * 100) : 0,
      }
    })

    // Lo que no entro en ninguna bolsa: pasa de la hora por tarea o cambia
    // estructura, asi que se cotiza aparte en vez de regalarse.
    const aCotizar = hojas.filter(t =>
      !valorDe(t, PREFIJO_BOLSA) && etiquetasDe(t).includes('a-cotizar'))

    return {
      porBolsa,
      aCotizar,
      horasACotizar: r2(aCotizar.reduce((s, t) => s + (t.horas_estimadas || 0), 0)),
      totalMes: r2(bolsas.reduce((s, b) => s + b.horas_mes, 0)),
      totalComprometido: r2(porBolsa.reduce((s, b) => s + b.comprometidas, 0)),
    }
  }, [tareas, bolsas, ciclo])

  if (bolsas.length === 0) return null

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <IconTimer size={16} />
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Bolsas de horas</h3>
        <select
          className="form-select"
          value={ciclo}
          onChange={e => setCiclo(e.target.value)}
          style={{ width: 'auto', padding: '2px 8px', fontSize: '0.82rem' }}
        >
          {ciclos.map(c => <option key={c} value={c}>{nombreDeCiclo(c)}</option>)}
        </select>
        <span className="text-muted" style={{ marginLeft: 'auto', fontSize: '0.85rem' }}>
          {consumo.totalComprometido} h comprometidas de {consumo.totalMes} h del mes
        </span>
      </div>

      <div className="bolsas-grid">
        {consumo.porBolsa.map(b => {
          const excedida = b.excedente > 0
          return (
            <div key={b.clave} className={`bolsa-card${excedida ? ' bolsa-card--excedida' : ''}`}>
              <div className="bolsa-card__head">
                <span className="bolsa-card__nombre">{b.nombre}</span>
                <span className={`bolsa-card__horas${excedida ? ' bolsa-card__horas--excedida' : ''}`}>
                  {b.comprometidas} / {b.horas_mes} h
                </span>
              </div>
              <div className="bolsa-card__barra">
                <div
                  className={`bolsa-card__relleno${excedida ? ' bolsa-card__relleno--excedida' : ''}`}
                  style={{ width: `${Math.min(100, b.pct)}%` }}
                />
              </div>
              <div className="bolsa-card__pie">
                {excedida ? (
                  <span className="bolsa-card__aviso">
                    <IconAlertTriangle size={12} /> {b.excedente} h sobre el tope
                  </span>
                ) : (
                  <span>{b.libres} h libres</span>
                )}
                <span>{b.hechas}/{b.tareas} tareas</span>
              </div>
              {b.descripcion && <p className="bolsa-card__detalle">{b.descripcion}</p>}
            </div>
          )
        })}
      </div>

      {consumo.aCotizar.length > 0 && (
        <div className="bolsa-aparte">
          <div className="bolsa-aparte__head">
            <IconFileText size={14} />
            <strong>Fuera de la bolsa — se cotiza aparte</strong>
            <span className="text-muted" style={{ marginLeft: 'auto' }}>
              {consumo.horasACotizar} h en {consumo.aCotizar.length} pedido{consumo.aCotizar.length > 1 ? 's' : ''}
            </span>
          </div>
          <ul className="bolsa-aparte__lista">
            {consumo.aCotizar.map(t => (
              <li key={t.id}>
                {t.titulo}
                <span className="text-muted"> · {t.horas_estimadas} h</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
